import type { AnimationData } from "@/types/replay"

const HOME = "da-smashaz"
const AWAY = "altdorf-eagles"

/**
 * Homepage pitch demo: one block opens a lane, blitzer scores.
 *
 * Pitch: 26 wide (0-25) x 15 tall (0-14).
 * Home (orcs) score at x=0 (left). Away (humans) score at x=25 (right).
 *
 * Rules for this data:
 * - ONE player moves per event (no simultaneous moves)
 * - Every `from` must match the player's actual position
 * - No two players may occupy the same coordinate at any point
 * - No standing enemy player adjacent to the running lane (y=7, x 0-6)
 *   so the score requires zero dodge rolls
 */
export const DEMO_ANIMATION_DATA: AnimationData = {
  homeTeamId: HOME,
  awayTeamId: AWAY,
  homeTeamName: "Da Smashaz",
  awayTeamName: "Altdorf Eagles",
  players: {
    // ── Orcs (8) ──
    "orc-bz": {
      abbr: "BZ",
      name: "Grimjaw",
      teamId: HOME,
      position: "Blitzer",
      ma: 6,
      st: 3,
      ag: 3,
      pa: null,
      av: 10,
      skills: ["Block"],
    },
    "orc-bo": {
      abbr: "BO",
      name: "Gutcruncha",
      teamId: HOME,
      position: "Black Orc Blocker",
      ma: 4,
      st: 4,
      ag: 4,
      pa: null,
      av: 10,
      skills: [],
    },
    "orc-l1": {
      abbr: "L1",
      name: "Gruntz",
      teamId: HOME,
      position: "Lineman",
      ma: 5,
      st: 3,
      ag: 3,
      pa: null,
      av: 10,
      skills: [],
    },
    "orc-l2": {
      abbr: "L2",
      name: "Mogdub",
      teamId: HOME,
      position: "Lineman",
      ma: 5,
      st: 3,
      ag: 3,
      pa: null,
      av: 10,
      skills: [],
    },
    "orc-l3": {
      abbr: "L3",
      name: "Skarfang",
      teamId: HOME,
      position: "Lineman",
      ma: 5,
      st: 3,
      ag: 3,
      pa: null,
      av: 10,
      skills: [],
    },
    "orc-l4": {
      abbr: "L4",
      name: "Urgok",
      teamId: HOME,
      position: "Lineman",
      ma: 5,
      st: 3,
      ag: 3,
      pa: null,
      av: 10,
      skills: [],
    },
    "orc-b2": {
      abbr: "B2",
      name: "Grotsnag",
      teamId: HOME,
      position: "Blitzer",
      ma: 6,
      st: 3,
      ag: 3,
      pa: null,
      av: 10,
      skills: ["Block"],
    },
    "orc-th": {
      abbr: "TH",
      name: "Skabrot",
      teamId: HOME,
      position: "Thrower",
      ma: 5,
      st: 3,
      ag: 3,
      pa: 3,
      av: 9,
      skills: ["Sure Hands", "Pass"],
    },
    // ── Humans (8) ──
    "hum-bz": {
      abbr: "BZ",
      name: "Karl Hagen",
      teamId: AWAY,
      position: "Blitzer",
      ma: 7,
      st: 3,
      ag: 3,
      pa: null,
      av: 9,
      skills: ["Block"],
    },
    "hum-l1": {
      abbr: "L1",
      name: "Werner",
      teamId: AWAY,
      position: "Lineman",
      ma: 6,
      st: 3,
      ag: 3,
      pa: null,
      av: 9,
      skills: [],
    },
    "hum-l2": {
      abbr: "L2",
      name: "Dieter",
      teamId: AWAY,
      position: "Lineman",
      ma: 6,
      st: 3,
      ag: 3,
      pa: null,
      av: 9,
      skills: [],
    },
    "hum-l3": {
      abbr: "L3",
      name: "Hans",
      teamId: AWAY,
      position: "Lineman",
      ma: 6,
      st: 3,
      ag: 3,
      pa: null,
      av: 9,
      skills: [],
    },
    "hum-ca": {
      abbr: "CA",
      name: "Fritz Renner",
      teamId: AWAY,
      position: "Catcher",
      ma: 8,
      st: 2,
      ag: 2,
      pa: null,
      av: 8,
      skills: ["Catch", "Dodge"],
    },
    "hum-l4": {
      abbr: "L4",
      name: "Otto",
      teamId: AWAY,
      position: "Lineman",
      ma: 6,
      st: 3,
      ag: 3,
      pa: null,
      av: 9,
      skills: [],
    },
    "hum-b2": {
      abbr: "B2",
      name: "Konrad",
      teamId: AWAY,
      position: "Blitzer",
      ma: 7,
      st: 3,
      ag: 3,
      pa: null,
      av: 9,
      skills: ["Block"],
    },
    "hum-th": {
      abbr: "TH",
      name: "Aldric",
      teamId: AWAY,
      position: "Thrower",
      ma: 6,
      st: 3,
      ag: 3,
      pa: 2,
      av: 9,
      skills: ["Sure Hands", "Pass"],
    },
  },
  events: [
    /*
     * STARTING POSITIONS (snapshot):
     *
     * Orcs driving left toward end zone (x=0):
     *   orc-l4  (4,5)    — screening ahead, upper
     *   orc-bo  (6,6)    — poised to blitz
     *   orc-bz* (6,7)    — ball carrier, center lane
     *   orc-l1  (6,8)    — cage corner
     *   orc-l2  (8,5)    — back support, upper flank
     *   orc-l3  (8,9)    — back support, lower flank
     *   orc-b2  (10,7)   — midfield reserve
     *   orc-th  (13,8)   — deep playmaker
     *
     * Humans defending:
     *   hum-ca  (1,3)    — deep safety, upper
     *   hum-l1  (2,5)    — covering end zone area
     *   hum-l2  (3,3)    — upper defensive zone
     *   hum-bz  (5,7)    — blocking the center lane  ← THE TARGET
     *   hum-l3  (4,11)   — lower defensive zone
     *   hum-l4  (7,4)    — upper midfield
     *   hum-b2  (9,9)    — lower midfield
     *   hum-th  (10,3)   — upper midfield
     *
     * Grimjaw needs 6 squares to score (= MA 6, no GFI).
     * No standing enemy player is adjacent to y=7 between x=0 and x=6.
     */

    // 0 — Snapshot: mid-drive, one defender in the lane
    {
      positionChanges: [],
      pitchSnapshot: {
        players: [
          // Orcs
          { id: "orc-bz", x: 6, y: 7, teamId: HOME, status: "standing" },
          { id: "orc-bo", x: 6, y: 6, teamId: HOME, status: "standing" },
          { id: "orc-l1", x: 6, y: 8, teamId: HOME, status: "standing" },
          { id: "orc-l2", x: 8, y: 5, teamId: HOME, status: "standing" },
          { id: "orc-l3", x: 8, y: 9, teamId: HOME, status: "standing" },
          { id: "orc-l4", x: 4, y: 5, teamId: HOME, status: "standing" },
          { id: "orc-b2", x: 10, y: 7, teamId: HOME, status: "standing" },
          { id: "orc-th", x: 13, y: 8, teamId: HOME, status: "standing" },
          // Humans
          { id: "hum-bz", x: 5, y: 7, teamId: AWAY, status: "standing" },
          { id: "hum-l1", x: 2, y: 5, teamId: AWAY, status: "standing" },
          { id: "hum-l2", x: 3, y: 3, teamId: AWAY, status: "standing" },
          { id: "hum-l3", x: 4, y: 11, teamId: AWAY, status: "standing" },
          { id: "hum-ca", x: 1, y: 3, teamId: AWAY, status: "standing" },
          { id: "hum-l4", x: 7, y: 4, teamId: AWAY, status: "standing" },
          { id: "hum-b2", x: 9, y: 9, teamId: AWAY, status: "standing" },
          { id: "hum-th", x: 10, y: 3, teamId: AWAY, status: "standing" },
        ],
        ball: { x: 6, y: 7, carrierId: "orc-bz" },
      },
      ballLanding: null,
      turnLabel: "Turn 7 — Da Smashaz",
      highlightType: null,
      actingPlayerId: null,
      actingTeamId: HOME,
      play: "Drive",
      detailHeader: "End Zone in Sight",
      detailNarrative:
        "Da Smashaz have the ball. One defender stands in the way.",
      detailMechanical: [],
    },

    // 1 — Black Orc steps into position for the hit
    // orc-bo: 6,6 → 5,6
    {
      positionChanges: [
        { type: "move", playerId: "orc-bo", from: [6, 6], to: [5, 6] },
      ],
      pitchSnapshot: null,
      ballLanding: null,
      turnLabel: null,
      highlightType: null,
      actingPlayerId: "orc-bo",
      actingTeamId: HOME,
      play: "Blitz",
      detailHeader: {
        player_name: "Gutcruncha",
        position: "Black Orc Blocker",
        action_type: "Blitz",
      },
      detailNarrative: "Gutcruncha lines up the hit on Karl Hagen.",
      detailMechanical: [],
    },

    // 2 — Block! Karl Hagen pushed sideways and knocked down
    // hum-bz: 5,7 → 5,8 (prone). Lane at y=7 is now CLEAR.
    {
      positionChanges: [
        { type: "push", playerId: "hum-bz", from: [5, 7], to: [5, 8] },
        { type: "status", playerId: "hum-bz", status: "prone" },
      ],
      pitchSnapshot: null,
      ballLanding: null,
      turnLabel: null,
      highlightType: null,
      actingPlayerId: "orc-bo",
      actingTeamId: HOME,
      play: "Block",
      detailHeader: {
        player_name: "Gutcruncha",
        position: "Black Orc Blocker",
        action_type: "Block",
      },
      detailNarrative: "WHAM! Karl Hagen goes flying! The lane is wide open!",
      detailMechanical: [
        { label: "Block", value: "Pow!" },
        { label: "Armour", value: "9+ \u2014 Broken!" },
        { label: "Injury", value: "Stunned" },
      ],
    },

    // 3 — Grimjaw charges through the gap
    // orc-bz: 6,7 → 5,7 → 4,7 → 3,7
    {
      positionChanges: [
        { type: "move", playerId: "orc-bz", from: [6, 7], to: [5, 7] },
        { type: "move", playerId: "orc-bz", from: [5, 7], to: [4, 7] },
        { type: "move", playerId: "orc-bz", from: [4, 7], to: [3, 7] },
      ],
      pitchSnapshot: null,
      ballLanding: null,
      turnLabel: null,
      highlightType: null,
      actingPlayerId: "orc-bz",
      actingTeamId: HOME,
      play: "Move",
      detailHeader: {
        player_name: "Grimjaw",
        position: "Blitzer",
        action_type: "Move",
      },
      detailNarrative: "Grimjaw bursts through the gap and charges downfield!",
      detailMechanical: [],
    },

    // 4 — TOUCHDOWN
    // orc-bz: 3,7 → 2,7 → 1,7 → 0,7
    {
      positionChanges: [
        { type: "move", playerId: "orc-bz", from: [3, 7], to: [2, 7] },
        { type: "move", playerId: "orc-bz", from: [2, 7], to: [1, 7] },
        { type: "move", playerId: "orc-bz", from: [1, 7], to: [0, 7] },
      ],
      pitchSnapshot: null,
      ballLanding: null,
      turnLabel: null,
      highlightType: "touchdown",
      actingPlayerId: "orc-bz",
      actingTeamId: HOME,
      play: "Touchdown",
      detailHeader: {
        player_name: "Grimjaw",
        position: "Blitzer",
        action_type: "Touchdown",
      },
      detailNarrative:
        "TOUCHDOWN! Grimjaw slams it home! Da Smashaz take the lead!",
      detailMechanical: [],
    },
  ],
}

/** Team color hues for the demo match */
export const DEMO_HOME_HUE = 145 // Orc green
export const DEMO_AWAY_HUE = 250 // Human blue
